import { useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { BuilderSlot } from "../../data/schedule-builder";
import { fetchScheduleSlotLineage, type ScheduleSlotLineage } from "../../data/schedule-seat-lineage";
import { OPEN_FILL_MODE_COPY, type OpenFillMode } from "../../../../../packages/shared/src/schedule-color";
import "./calendar-panel.css";

type Action = "assign" | "replace" | "revise" | "cancel";

export function CalendarShiftPanel({ client, accountId, slot, siteName, routeName, timeZone, fillMode, canManage, onAction, onClose }: {
  client: SupabaseClient; accountId: string; slot: BuilderSlot; siteName: string; routeName: string;
  timeZone: string; fillMode: OpenFillMode; canManage: boolean; onAction: (action: Action) => void; onClose: () => void;
}) {
  const [lineage, setLineage] = useState<ScheduleSlotLineage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let current = true;
    setLineage(null); setError(null);
    void fetchScheduleSlotLineage(client, accountId, slot.id).then(result => {
      if (!current) return;
      if (result.ok) setLineage(result.lineage); else setError(result.reason);
    });
    return () => { current = false; };
  }, [client, accountId, slot.id, attempt]);
  const instant = (value: string) => new Intl.DateTimeFormat("en-US", { timeZone, month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(value));
  return (
    <aside className="builder__drawer calendar-shift-panel" role="dialog" aria-modal="true" aria-label={`Shift details - ${siteName}, ${routeName}`}>
      <div className="builder__drawer-head"><h3 className="builder__drawer-title">Shift details</h3><button type="button" className="builder__navbtn" onClick={onClose}>Close</button></div>
      <p className="calendar-shift-panel__site">{siteName}</p><h4>{routeName}</h4>
      <p>{slot.startsAt ? instant(slot.startsAt) : "Time unavailable"} → {slot.endsAt ? instant(slot.endsAt) : "Time unavailable"}</p>
      <p className="calendar-shift-panel__muted">{timeZone} · {slot.fraction} assigned</p>
      <p>Open-seat policy: {OPEN_FILL_MODE_COPY[fillMode]}. Workspace settings control officer self-service.</p>
      {canManage ? <div className="calendar-shift-panel__actions">
        {slot.assignable ? <button type="button" className="minter__confirm-go" onClick={() => onAction("assign")}>Assign officer</button> : null}
        {slot.replaceable ? <button type="button" className="builder__navbtn" onClick={() => onAction("replace")}>Replace officer</button> : null}
        {slot.reviseBoundsEligible ? <button type="button" className="builder__navbtn" onClick={() => onAction("revise")}>Revise times for all staff</button> : null}
        {slot.cancellable ? <button type="button" className="builder__cancel" onClick={() => onAction("cancel")}>Cancel shift for all staff</button> : null}
      </div> : <p>Schedule changes are unavailable right now. Check your access or try again.</p>}
      <p>Time and headcount edits affect the whole shift and its future scheduled assignments. Cancelling this shift cancels all scheduled staff assignments; records remain in history.</p>
      <h4>Recorded seats and history</h4>
      {error ? <div><p role="alert" tabIndex={-1}>{error}</p><button type="button" className="builder__navbtn" onClick={() => setAttempt(n => n + 1)}>Retry history</button></div> : !lineage ? <p role="status">Loading shift history…</p> : <ul className="calendar-shift-panel__seats">
        {lineage.seats.map(seat => <li key={seat.seatOrdinal}>
          <strong>Seat {seat.seatOrdinal}</strong><span>{seat.fillState === "cancelled" ? "Cancelled after publish" : seat.fillState === "open" ? "Open" : seat.memberLabel ?? "Assigned"}</span>
          {seat.dutyIntervals.map((interval, index) => <p key={index}>{interval.personLabel} · {instant(interval.start)} → {instant(interval.end)} · {interval.lineageTag.replaceAll("_", " ")}</p>)}
        </li>)}
      </ul>}
    </aside>
  );
}
